import { parsePhoneNumberFromString } from 'libphonenumber-js';

const regionNames =
  typeof Intl !== 'undefined' && Intl.DisplayNames
    ? new Intl.DisplayNames(['en'], { type: 'region' })
    : null;

/**
 * Convert ISO 3166-1 alpha-2 code to flag emoji (e.g. "IN" -> 🇮🇳).
 */
export function iso2ToFlagEmoji(iso2) {
  if (!iso2 || iso2.length !== 2) {
    return '';
  }

  return iso2
    .toUpperCase()
    .replace(/./g, (char) => String.fromCodePoint(127397 + char.charCodeAt(0)));
}

/**
 * Resolve country metadata from a WhatsApp mobile number (digits only, no +).
 *
 * @param {string|number|null|undefined} mobileNumber
 * @param {'domestic'|'international'} [recipientMode='domestic']
 * @returns {{ iso2: string, name: string, flag: string, dialCode?: string } | null}
 */
export function resolvePhoneCountry(mobileNumber, recipientMode = 'domestic') {
  const digits = String(mobileNumber ?? '').replace(/\D/g, '');
  if (!digits) {
    return null;
  }

  const defaultCountry = recipientMode === 'domestic' ? 'IN' : undefined;
  const parsed = parsePhoneNumberFromString(`+${digits}`, defaultCountry);

  if (!parsed?.country) {
    return null;
  }

  const iso2 = parsed.country;

  return {
    iso2,
    name: regionNames?.of(iso2) || iso2,
    flag: iso2ToFlagEmoji(iso2),
    dialCode: parsed.countryCallingCode ? `+${parsed.countryCallingCode}` : undefined,
  };
}

/**
 * Aggregate recipient counts by country (sorted by count desc, then name).
 *
 * @param {Array<string|number>} mobileNumbers
 * @param {'domestic'|'international'} [recipientMode='domestic']
 * @returns {Array<{ iso2: string, name: string, flag: string, count: number }>}
 */
export function buildCountrySummary(mobileNumbers, recipientMode = 'domestic') {
  if (!Array.isArray(mobileNumbers) || mobileNumbers.length === 0) {
    return [];
  }

  const byIso = new Map();

  for (const mobileNumber of mobileNumbers) {
    const country = resolvePhoneCountry(mobileNumber, recipientMode);
    if (!country) {
      continue;
    }

    const existing = byIso.get(country.iso2);
    if (existing) {
      existing.count += 1;
      continue;
    }

    byIso.set(country.iso2, {
      iso2: country.iso2,
      name: country.name,
      flag: country.flag,
      count: 1,
    });
  }

  return Array.from(byIso.values()).sort((a, b) => {
    if (b.count !== a.count) {
      return b.count - a.count;
    }

    return a.name.localeCompare(b.name);
  });
}
